import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook that tracks page visibility using the Page Visibility API.
 * Returns whether the page is currently visible and provides
 * a grace-period system for the plant-wilt mechanic.
 *
 * @param {Object} options
 * @param {number} options.gracePeriodMs - Grace period before triggering death (default 30000ms)
 * @param {Function} options.onGracePeriodExpired - Called when grace period expires (plant wilts)
 * @param {boolean} options.enabled - Whether the visibility tracking is active
 */
export function useVisibility({ gracePeriodMs = 30000, onGracePeriodExpired, enabled = false } = {}) {
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const [isWilting, setIsWilting] = useState(false);
  const [wiltStartTime, setWiltStartTime] = useState(null);
  const graceTimerRef = useRef(null);
  const wiltTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
    if (wiltTimerRef.current) {
      clearTimeout(wiltTimerRef.current);
      wiltTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setIsWilting(false);
      setWiltStartTime(null);
      return;
    }

    function handleVisibilityChange() {
      const hidden = document.hidden;
      setIsVisible(!hidden);

      if (hidden) {
        // Start wilting immediately
        setIsWilting(true);
        setWiltStartTime(Date.now());

        // Set grace period timer for plant wilting
        clearTimers();
        graceTimerRef.current = setTimeout(() => {
          if (onGracePeriodExpired) {
            onGracePeriodExpired();
          }
        }, gracePeriodMs);

        // Send notification if permissions granted
        if (Notification.permission === 'granted') {
          try {
            new Notification('Your plant is wilting!', {
              body: 'Return to Pomogrow to save your plant.',
              icon: '/logo192.png',
              tag: 'plant-wilting',
            });
          } catch {
            // Notification API may not be available in all contexts
          }
        }
      } else {
        // User returned — save the plant
        clearTimers();
        setIsWilting(false);
        setWiltStartTime(null);
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimers();
    };
  }, [enabled, gracePeriodMs, onGracePeriodExpired, clearTimers]);

  return { isVisible, isWilting, wiltStartTime };
}
